'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  trackUnitImageView,
  trackUnitPageActive,
  trackUnitPageInactive,
  type UnitEngagementSurface,
} from '@repo/api';
import {
  UNIT_ENGAGEMENT_CONFIG,
  createImageBrowseReporter,
  type ImageBrowseReporter,
} from '@repo/utils';
import { useStore } from '@/providers/StoreProvider';

function createVisitId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Reports listing-card image browsing as a short unit "visit" for logged-in
 * users. On the first image navigation of a card it mirrors a unit-page open:
 *   - `track_image_view` — seeds the BE drop-off counter ("unit browsed"). A
 *     dedicated endpoint (not `track_view`) so browse data can't pollute the
 *     plain unit-page view metrics.
 *   - `page_active` — opens the engagement window (fresh `visitId`).
 * then one `page_inactive` when browsing that card stops (idle) or the surface
 * unmounts.
 *
 * The server owns all drop-off policy (the 5-minute grouping with a later
 * same-unit page visit, dedup, and the schedule-tour / open-house exclusion) —
 * this only reports the view + engagement.
 *
 * Pass the `surface` the cards live on (e.g. `listing_results`) — it rides on
 * every `page_active`/`page_inactive` so the server can apply the right rules.
 *
 * Returns `reportImageBrowse(unitId)` to call from a card's quickview handler
 * (the same gesture that fires `*_listingcard_quickview`).
 */
export function useImageBrowseReporter(surface: UnitEngagementSurface): {
  reportImageBrowse: (unitId: string) => void;
} {
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const sessionHydrated = useStore(s => s.webAuth.sessionHydrated);
  // Both token shapes count as a bearer. Firing before one lands gets a 403,
  // which the response interceptor treats as a dead session.
  const authToken = useStore(s => s.webAuth.token);
  const authCustomToken = useStore(s => s.webAuth.customToken);

  const enabled =
    UNIT_ENGAGEMENT_CONFIG.enabled &&
    sessionHydrated &&
    isLoggedIn &&
    Boolean(authToken || authCustomToken);

  // Read at cleanup time to tell "left the surface" from "logged out". The
  // latter has no bearer left, so reporting the browse would only earn a 403.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const reporterRef = useRef<ImageBrowseReporter | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const reporter = createImageBrowseReporter({
      createVisitId,
      onActive: ({ unitId, visitId }) => {
        // Seed the browse into the drop-off counter, then open the engagement
        // window — same pair a unit-page open fires, minus the shared metrics.
        void trackUnitImageView(unitId).catch(() => undefined);
        void trackUnitPageActive(unitId, { visitId, surface }).catch(() => undefined);
      },
      onInactive: ({ unitId, visitId }) => {
        // Always keepalive: a browse can end because the surface is tearing
        // down, which cancels an ordinary fetch. No-op while the page is alive.
        void trackUnitPageInactive(unitId, { visitId, surface }, { keepalive: true }).catch(
          () => undefined,
        );
      },
    });
    reporterRef.current = reporter;

    return () => {
      // Leaving the surface ends any browse still open (reports page_inactive).
      if (enabledRef.current) reporter.endAll();
      reporter.dispose();
      reporterRef.current = null;
    };
  }, [enabled, surface]);

  const reportImageBrowse = useCallback((unitId: string) => {
    reporterRef.current?.browse(unitId);
  }, []);

  return { reportImageBrowse };
}
